
import React, { useState } from 'react'
import { X, Gift, Star } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import Sidebar from '../components/Sidebar'
import { useRewards } from '@/hooks/useRewards'

// Domain options
const domains = [
  { value: 'self', label: 'Self' },
  { value: 'wealth', label: 'Wealth' },
  { value: 'social', label: 'Social' }
]

const Rewards = () => {
  const [selectedDomain, setSelectedDomain] = useState('all')
  const [selectedLevel, setSelectedLevel] = useState(1)
  const [selectedReward, setSelectedReward] = useState<any>(null)
  const [clickedRewardLevel, setClickedRewardLevel] = useState(1)
  const [isModalOpen, setIsModalOpen] = useState(false)
  
  const { 
    rewards, 
    userRewards, 
    loading, 
    getRewardsByDomain, 
    getUserRewardLevel,
    hasUserEarnedReward 
  } = useRewards()

  // Get user's current level from their profile
  const getUserCurrentLevel = () => {
    // This should return the user's current level (1, 2, or 3)
    // TODO: Implement actual user level logic based on points/completed modules
    // For now, returning 1 to test level restrictions
    return 1
  }

  const userCurrentLevel = getUserCurrentLevel()

  const filteredRewards = selectedDomain && selectedDomain !== 'all'
    ? getRewardsByDomain(selectedDomain)
    : rewards

  // Filter rewards based on selected level and user's access
  const getRewardsForLevel = (level: number) => {
    // If user tries to access a level higher than their current level, return empty array
    if (level > userCurrentLevel) {
      return []
    }
    
    return filteredRewards.filter(reward => {
      // Check if the reward has a discount for this level
      const discount = getDiscountText(reward, level)
      return discount && discount !== 'No discount available'
    })
  }

  const getDiscountText = (reward: any, level: number) => {
    switch (level) {
      case 1:
        return reward.level_1_discount
      case 2:
        return reward.level_2_discount
      case 3:
        return reward.level_3_discount
      default:
        return 'No discount available'
    }
  }

  const currentLevelRewards = getRewardsForLevel(selectedLevel)

  const handleRewardClick = (reward: any) => {
    setSelectedReward(reward)
    setClickedRewardLevel(selectedLevel)
    setIsModalOpen(true)
  }

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'self':
        return 'bg-blue-500 text-white'
      case 'wealth':
        return 'bg-green-500 text-white'
      case 'social':
        return 'bg-purple-500 text-white'
      default:
        return 'bg-gray-500 text-white'
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">Loading rewards...</div>
          </div>
        </main>
      </div>
    )
  }

  // Check if there are no rewards at all
  if (rewards.length === 0) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Rewards</h1>
              <p className="text-gray-600">Unlock exclusive discounts and offers by completing modules</p>
            </div>

            {/* No Rewards Message */}
            <div className="text-center py-12">
              <Gift className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No rewards available yet</h3>
              <p className="text-gray-600 mb-4">
                We're working on bringing you exciting rewards and exclusive offers. 
                Complete modules to unlock amazing benefits when they become available!
              </p>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Rewards</h1>
            <p className="text-gray-600">Unlock exclusive discounts and offers by completing modules</p>
          </div>

          {/* Domain Filter */}
          <div className="mb-6">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Select Domain:</span>
              <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Domains" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Domains</SelectItem>
                  {domains.map(domain => (
                    <SelectItem key={domain.value} value={domain.value}>
                      {domain.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Level Tabs */}
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Select Level:</span>
              {[1, 2, 3].map(level => (
                <Button
                  key={level}
                  variant={selectedLevel === level ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedLevel(level)}
                  disabled={level > userCurrentLevel}
                  className={`min-w-[80px] ${
                    level > userCurrentLevel 
                      ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400' 
                      : ''
                  }`}
                >
                  Level {level}
                  {level > userCurrentLevel && (
                    <span className="ml-1 text-xs text-gray-400">🔒</span>
                  )}
                </Button>
              ))}
            </div>
          </div>

          {/* Level Rewards Heading */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Level {selectedLevel} Rewards</h2>
          </div>

          {/* Rewards Grid */}
          {currentLevelRewards.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentLevelRewards.map((reward) => {
                const userLevel = getUserRewardLevel(reward.id)
                const hasEarned = hasUserEarnedReward(reward.id)
                
                return (
                  <div 
                    key={reward.id} 
                    className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer relative"
                    onClick={() => handleRewardClick(reward)}
                  >
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4 z-10">
                      <Badge className={getCategoryColor(reward.domain_type)}>
                        {reward.domain_type}
                      </Badge>
                    </div>

                    {/* Reward Content */}
                    <div className="p-6 pt-16">
                      {/* Mock Product Image/Logo Placeholder */}
                      <div className="w-full h-32 bg-gradient-to-r from-blue-400 to-purple-500 rounded-lg mb-4 flex items-center justify-center">
                        <span className="text-white font-bold text-lg">{reward.account_type}</span>
                      </div>

                      {/* Title/Offer */}
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2">
                        {reward.account_type}
                      </h3>

                      {/* Subheader */}
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {reward.subheader || 'Exclusive offer for Dably users. Complete modules to unlock this reward.'}
                      </p>

                      {/* User Progress */}
                      {hasEarned && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-green-800">
                              Level {userLevel} Achieved!
                            </span>
                            <Badge variant="secondary" className="bg-green-100 text-green-800">
                              {getDiscountText(reward, userLevel)}
                            </Badge>
                          </div>
                        </div>
                      )}

                      {/* Information Icon - Bottom Right */}
                      <div className="absolute bottom-4 right-4 z-10">
                        <div 
                          className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-purple-600 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRewardClick(reward)
                          }}
                        >
                          <span className="text-white text-sm font-medium">i</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Gift className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No rewards found</h3>
              <p className="text-gray-600">
                No Level {selectedLevel} rewards available for the selected domain. Try selecting a different level, domain, or check back later for new offers.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Reward Detail Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-yellow-500" />
              {selectedReward?.account_type} - Level {clickedRewardLevel}
            </DialogTitle>
          </DialogHeader>
          
          {selectedReward && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <Badge className={`${getCategoryColor(selectedReward.domain_type)} mb-3`}>
                  {selectedReward.domain_type}
                </Badge>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {selectedReward.account_type}
                </h3>
                {selectedReward.subheader && (
                  <p className="text-lg text-gray-700 mb-4">
                    {selectedReward.subheader}
                  </p>
                )}
              </div>

              {/* Description */}
              {selectedReward.description && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Description</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700">{selectedReward.description}</p>
                  </div>
                </div>
              )}



              {/* Level-based Discounts */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Your Reward</h4>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-green-800">Level {clickedRewardLevel}</span>
                    <span className="text-green-600 font-semibold text-lg">
                      {getDiscountText(selectedReward, clickedRewardLevel)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Terms and Conditions */}
              {selectedReward.terms_conditions && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Terms & Conditions</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: selectedReward.terms_conditions }}
                    />
                  </div>
                </div>
              )}

              {/* User Progress */}
              {hasUserEarnedReward(selectedReward.id) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-medium text-green-800 mb-2">Your Progress</h4>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-700">
                      You've achieved Level {getUserRewardLevel(selectedReward.id)}
                    </span>
                    <Badge variant="secondary" className="bg-green-100 text-green-800">
                      {getDiscountText(selectedReward, getUserRewardLevel(selectedReward.id))}
                    </Badge>
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Rewards
